import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { showModalSuccess } from "../../../../redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";

const urlBase = "api/contract_values";

type RequestParams = Record<string, any>;

const getOptionsArray = (payload: any) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

export interface ContractValuePayload {
  minimo: number | string;
  maximo: number | string;
  from?: string;
  provider_id?: number | string;
  [key: string]: any;
}

export interface ContractValueState {
  data: any;
  suggestions: any[];
  defaultValues: ContractValuePayload | null;
  currentPage: number;
  params: RequestParams;
  meta: any;
  total: number;
}

export const getContractValues = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("contractValues/getAll", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(urlBase, { params: query });
  return {
    params: query,
    data: response.data
  };
});

export const getContractValueSuggestions = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("contractValues/getSuggestions", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(`${urlBase}/suggestions`, { params: query });
  return {
    data: response.data
  };
});

export const getContractValueById = createAsyncThunk<any, number>(
  "contractValues/getById",
  async (id: number) => {
    const response = await api.get(`${urlBase}/${id}`);
    return {
      data: response.data
    };
  }
);

export const createContractValue = createAsyncThunk<
  any,
  ContractValuePayload,
  { dispatch: AppDispatch; state: RootState }
>("contractValues/create", async (payload, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const body = {
    provider_id: payload.provider_id,
    business_line_id: businessLineSelected?.id,
    product_id: productSelected?.value,
    minimum_value: payload.minimo,
    maximum_value: payload.maximo,
    status: true
  };

  const response = await api.post(urlBase, body);

  if (response.data.status === "success") {
    if (payload.from === "providers") {
      dispatch(getContractValues({ format: "options", free: true }));
    }
    await dispatch(
      showModalSuccess(
        payload.from === "providers"
          ? { message: response.data.message }
          : { message: response.data.message, url: "goBack" }
      )
    );
  }

  return {
    data: response.data
  };
});

export const updateContractValue = createAsyncThunk<
  any,
  { id: number | string; data: ContractValuePayload },
  { dispatch: AppDispatch }
>("contractValues/update", async ({ id, data }, { dispatch }) => {
  const payload = {
    provider_id: data.provider_id,
    business_line_id: data.business_line_id,
    product_id: data.product_id,
    minimum_value: data.minimo,
    maximum_value: data.maximo,
    status: true
  };
  const response = await api.put(`${urlBase}/${id}`, payload);

  if (response.data.status === "success") {
    const url = "goBack";
    dispatch(showModalSuccess({ message: response.data.message, url }));
  }

  return {
    data: response.data
  };
});

export const deleteContractValue = createAsyncThunk<
  any,
  number,
  { dispatch: AppDispatch }
>("contractValues/delete", async (id, { dispatch }) => {
  const response = await api.delete(`${urlBase}/${id}`);

  if (response.data.status === "success") {
    dispatch(showModalSuccess({ message: response.data.message }));
    await dispatch(getContractValues());
  }

  return {
    data: response.data
  };
});

const initialState: ContractValueState = {
  data: [],
  suggestions: [],
  defaultValues: null,
  currentPage: 1,
  params: {},
  meta: {},
  total: 0
};

export const contractValuesSlice = createSlice({
  name: "contractValues",
  initialState,
  reducers: {
    resetContractValues: (state) => {
      state.data = [];
      state.suggestions = [];
      state.defaultValues = null;
      state.currentPage = 1;
      state.params = {};
      state.meta = {};
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getContractValues.fulfilled, (state, action) => {
      if (action.payload?.params?.format === "options") {
        state.suggestions = getOptionsArray(action.payload.data);
      } else {
        state.data = action.payload.data;
        state.currentPage = action.payload.data?.current_page;
        state.params = action.payload.params;
        state.meta = action.payload.data?.meta;
        state.total = action.payload.data?.meta?.total;
      }
    });
    builder.addCase(getContractValueById.fulfilled, (state, action) => {
      const payloadData = action.payload?.data?.data || {};
      state.defaultValues = {
        minimo: payloadData.minimum_value ?? payloadData.min ?? 0,
        maximo: payloadData.maximum_value ?? payloadData.max ?? 0,
        aseguradora: payloadData.aseguradora ?? payloadData.insurer ?? "",
        provider_id: payloadData.provider_id ?? payloadData.provider?.id ?? ""
      };
    });
    builder.addCase(
      getContractValueSuggestions.fulfilled,
      (state, action) => {
        state.suggestions = getOptionsArray(action.payload.data);
      }
    );
  }
});

export const { resetContractValues } = contractValuesSlice.actions;

export default contractValuesSlice.reducer;
